import { Box, Button, Menu, MenuItem, styled, Tooltip } from '@mui/material'
import { Refresh } from '@mui/icons-material'
import { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { AppContext } from '../../../App'
import { INITIAL_CANDLE_COUNT, MULTIPANE_FULLSCREEN_MODE_ID } from '../../../assets/constants'
import SplitHorizontalIcon from '../../../assets/icons/SplitHorizontalIcon'
import SplitVerticalIcon from '../../../assets/icons/SplitVerticalIcon'
import { useDispatch, useSelector } from 'react-redux'
import { selectActiveWidget, selectLayout, selectLayoutWidgets } from '../../../selectors/widgetDashboard'
import { selectActiveLayout, selectActiveLayoutId } from '../../../selectors/home'
import { WidgetTypes, WidgetTypesNames } from '../../widgetDashboard/widgetDashboard.enums'
import { deleteWidget, updateWidgetType, updateWidgetSettings } from '../../../reducer/widgetDashboard.reducer'
import { NonInteractiveTooltip } from '../../custom'
import BinIcon from '../../../assets/icons/BinIcon'
import ThinArrowLeftIcon from '../../../assets/icons/ThinArrowLeftIcon'
import ThinArrowRightIcon from '../../../assets/icons/ThinArrowRightIcon'
import AddIcon from '../../../assets/icons/AddIcon'
import SubtractIcon from '../../../assets/icons/SubtractIcon'
import Widget from '../../../assets/icons/widget'
import FullScreenIcon from '../../../assets/icons/FullScreenIcon'
import { FullScreenToggle, selectMultiPaneFullScreen, selectWidgetPaneId } from '../../../selectors/common'
import Chart from '../chart'
import { ChartEvents } from '../chart.events'
import { IChartApi } from 'lightweight-charts'
import { ShortcutsCategories, ShortcutType } from '../../KeyboardShortcuts/Shortcuts.types'
import { ChartSettingsShortcutsKeyName } from '../../KeyboardShortcuts/ShortcutsKeymap'
import useShortcuts from '../../../hooks/useShortcuts'
import { setWidgetsPaneId, toggleFullscreen } from '../../../actions/common.actions'
import FullScreenReverseIcon from '../../../assets/icons/FullScreenReverseIcon'
import { Security, SecurityCategory_CategoryCode } from '../../../proto/generated/security'
import { useResizeDetector } from 'react-resize-detector'
import { ActionType, useUndoRedoContext } from '../../UndoRedoFunctonalities/UndoRedo.context'
import { WidgetDashboardConfig } from '../../widgetDashboard/widgetDashboard.interface'
import AnalyticsManager from '../../../analytics/AnalyticsManager'
import { getSecurityAsync, getSecurityKey } from '../../../util/security'
import { selectPrimarySecurity } from '../../../selectors/home'
interface ChartSettingsButtonsProps {
    allowSplit?: boolean,
    security: Security;
    viewKey: string;
}

const CustomButton = styled(Button)(({ theme }) => ({
    maxHeight: '25px',
    minHeight: '25px',
    maxWidth: '25px',
    minWidth: '25px',
    padding: 0,
    margin: '2px',
    borderRadius: '2px',
    background: theme.palette.background.paper,
    backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.05))',
    color: theme.palette.text.primary,
    pointerEvents: 'auto',
    '&:hover': {
        background: theme.palette.background.paper
    }
}))
const ChartSettingsButtons = ({ viewKey, security, allowSplit }: ChartSettingsButtonsProps) => {
    const { push } = useUndoRedoContext()
    const dispatch = useDispatch()
    const [open, setOpen] = useState(false)
    const appContext = useContext(AppContext)
    const activeLayoutId = useSelector(selectActiveLayoutId)
    const layout = useSelector(selectActiveLayout)
    const layoutRef = useRef<WidgetDashboardConfig>(null)
    const widgets = useSelector(selectLayoutWidgets(activeLayoutId))
    const activeWidget = useSelector(selectActiveWidget(activeLayoutId))
    const { key } = useSelector(selectPrimarySecurity)
    const [mainsecurity, setMainsecurity] = useState<Security | undefined>()
    useEffect(() => {
        if (!key) return
        getSecurityAsync(key).then(setMainsecurity)
    }, [key])
    const currentWidget = useMemo(() => widgets[Number(viewKey.split('-')[1]) ?? 0], [viewKey, widgets])
    const [widgetType, setWidgetType] = useState<WidgetTypes>(currentWidget?.widgetType)
    useEffect(() => {
        if (currentWidget?.widgetType != null) {
            setWidgetType(currentWidget.widgetType)
        }
    }, [currentWidget?.widgetType])
    const chart = appContext.getChart(viewKey)
    const [drawingInteractionActive, setDrawingInteractionActive] = useState(false)
    const chartApi: IChartApi = useMemo(() => {
        let chartApi = appContext.getChart(viewKey)?.chartApi
        if (widgetType === WidgetTypes.CHART) {
            chartApi = appContext.getChart(viewKey)?.chartApi
        } else if (widgetType === WidgetTypes.OI_CHART) {
            chartApi = appContext.getOiChart(viewKey)
        } else if (widgetType === WidgetTypes.MULTI_STRIKE_CHART) {
            chartApi = appContext.getMultiStrikeChart(viewKey)?.chartApi
        }
        return chartApi
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appContext, appContext.oiChart[viewKey], appContext.chart[viewKey], appContext.multiStrikeChart[viewKey], widgetType, viewKey])
    const btnRef = useRef<HTMLButtonElement | null>(null)
    const btnContainerRef = useRef<HTMLDivElement | null>(null)
    const drawingInteractionActiveRef = useRef(false)
    const paneTypeMenuOpenRef = useRef(false)
    const [paneTypeMenuOpen, setPaneTypeMenuOpen] = useState(false)
    const activeLayout = useSelector(selectLayout(activeLayoutId))
    const selectedPaneId = useSelector(selectWidgetPaneId)
    const isWidgetFullScreen = useSelector(FullScreenToggle)
    const timeScale = useMemo(() => chartApi?.timeScale(), [chartApi])
    const resolveChartElement = useCallback(() => {
        return (
            document.getElementById(`chart-${viewKey}`) ||
            document.getElementById(viewKey) ||
            btnContainerRef.current?.closest('.chart-selector') ||
            (btnContainerRef.current?.offsetParent as HTMLElement | null) ||
            null
        )
    }, [viewKey])

    const targetRef = useRef<HTMLElement | null>(resolveChartElement())

    useResizeDetector({ targetRef: targetRef as any })
    useEffect(() => {
        targetRef.current = resolveChartElement()
        // Chart mount node can appear shortly after buttons mount
        const timer = window.setTimeout(() => {
            targetRef.current = resolveChartElement()
        }, 0)
        return () => window.clearTimeout(timer)
    }, [resolveChartElement])

    useEffect(() => {
        layoutRef.current = layout
    }, [layout])

    useEffect(() => {
        paneTypeMenuOpenRef.current = paneTypeMenuOpen
    }, [paneTypeMenuOpen])

    useEffect(() => {
        drawingInteractionActiveRef.current = drawingInteractionActive
        const el = btnContainerRef.current
        if (!el) return
        if (drawingInteractionActive) {
            el.style.opacity = '0'
            el.style.visibility = 'hidden'
        } else {
            el.style.visibility = 'visible'
        }
    }, [drawingInteractionActive])

    useEffect(() => {
        // Drawing manager only exists on candle Chart instances
        if (widgetType && widgetType !== WidgetTypes.CHART) {
            setDrawingInteractionActive(false)
            return
        }

        let cancelled = false
        let chartInstance = appContext.getChart(viewKey)
        let retryTimer: ReturnType<typeof setTimeout> | undefined
        let attempts = 0

        const syncDrawingInteraction = () => {
            const dm = appContext.getChart(viewKey)?.drawingManager
            if (!dm) {
                setDrawingInteractionActive(false)
                return
            }
            setDrawingInteractionActive(Boolean(dm.getActiveDrawing() || dm.getFocusedDrawing()))
        }

        const onDrawingActive = () => setDrawingInteractionActive(true)
        const onDrawingInactive = () => syncDrawingInteraction()
        const onDrawingFocused = () => syncDrawingInteraction()
        const onDrawingDestroyed = () => syncDrawingInteraction()

        const unsubscribe = (instance: Chart | undefined) => {
            if (!instance) return
            instance.unsubscribeEvent(ChartEvents.DRAWING_ACTIVE, onDrawingActive)
            instance.unsubscribeEvent(ChartEvents.DRAWING_INACTIVE, onDrawingInactive)
            instance.unsubscribeEvent(ChartEvents.DRAWING_FOCUSED, onDrawingFocused)
            instance.unsubscribeEvent(ChartEvents.DRAWING_DESTROYED, onDrawingDestroyed)
        }

        const subscribe = (instance: Chart) => {
            instance.subscribeEvent(ChartEvents.DRAWING_ACTIVE, onDrawingActive)
            instance.subscribeEvent(ChartEvents.DRAWING_INACTIVE, onDrawingInactive)
            instance.subscribeEvent(ChartEvents.DRAWING_FOCUSED, onDrawingFocused)
            instance.subscribeEvent(ChartEvents.DRAWING_DESTROYED, onDrawingDestroyed)
            syncDrawingInteraction()
        }

        const trySubscribe = () => {
            if (cancelled) return
            chartInstance = appContext.getChart(viewKey)
            if (!chartInstance?.drawingManager) {
                attempts += 1
                if (attempts < 50) {
                    retryTimer = setTimeout(trySubscribe, 100)
                }
                return
            }
            subscribe(chartInstance)
        }

        trySubscribe()

        return () => {
            cancelled = true
            if (retryTimer) clearTimeout(retryTimer)
            unsubscribe(chartInstance)
        }
    }, [appContext, viewKey, widgetType])

    useEffect(() => {
        const HOVER_PADDING = 40
        const BOTTOM_BAND = 120
        const handleMouseMove = (e: MouseEvent) => {
            const el = btnContainerRef.current
            if (!el || drawingInteractionActiveRef.current) return

            if (paneTypeMenuOpenRef.current) {
                el.style.opacity = '1'
                return
            }

            // Scope hit-testing to this pane's chart so multipane slots don't share one target
            const chartEl = resolveChartElement()
            targetRef.current = chartEl
            const chartRect = chartEl?.getBoundingClientRect()
            const btnRect = el.getBoundingClientRect()

            const nearButtons =
                btnRect.width > 0 &&
                btnRect.height > 0 &&
                e.clientX >= btnRect.left - HOVER_PADDING &&
                e.clientX <= btnRect.right + HOVER_PADDING &&
                e.clientY >= btnRect.top - HOVER_PADDING &&
                e.clientY <= btnRect.bottom + HOVER_PADDING

            const inChartBottomBand =
                !!chartRect &&
                chartRect.width > 0 &&
                chartRect.height > 0 &&
                e.clientX >= chartRect.left &&
                e.clientX <= chartRect.right &&
                e.clientY >= chartRect.bottom - BOTTOM_BAND &&
                e.clientY <= chartRect.bottom

            el.style.opacity = nearButtons || inChartBottomBand ? '1' : '0'
        }

        window.addEventListener('mousemove', handleMouseMove)
        return () => {
            window.removeEventListener('mousemove', handleMouseMove)
        }
    }, [resolveChartElement])

    const showResetChartButton = useMemo(() => {
        try {
            if (widgetType === WidgetTypes.CHART && timeScale) {
                const currentVisibleLogicalRange = timeScale.getVisibleLogicalRange()
                const candles = (chart as Chart).getRenderedPrimaryData()
                const currentFrom = Math.max(candles.length - INITIAL_CANDLE_COUNT, 0)
                const currentTo = candles.length + 3
                const expectedFrom = currentVisibleLogicalRange?.from as number
                const expectedTo = currentVisibleLogicalRange?.to as number
                if ((currentFrom === expectedFrom && currentTo === expectedTo) && chartApi.priceScale('right').options().autoScale) {
                    return false
                }
                return true
            }
        } catch (e) {
            return true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chart])

    const resetTimeScale = useCallback(() => {
        AnalyticsManager.logEvent('Reset Chart Clicked', { source: 'chart-hovered-buttons' })
        appContext.getChart(viewKey).resetTimeScale()
    }, [appContext, viewKey])

    const splitHorizontal = useCallback(() => {
        currentWidget && appContext.dashboard?.splitPane(currentWidget.slotId, 2, true)
    }, [currentWidget, appContext])

    const splitVertical = useCallback(() => {
        currentWidget && appContext.dashboard?.splitPane(currentWidget.slotId, 2, false)
    }, [currentWidget, appContext])

    const deleteCurrentWidget = useCallback(() => {
        AnalyticsManager.logEvent('Delete Widget Clicked', { source: 'chart-hovered-buttons' })
        if (!activeLayout?.singleMode) {
            currentWidget && appContext.dashboard?.deletePane(currentWidget.slotId)
            dispatch(deleteWidget({
                id: activeLayoutId,
                slotId: currentWidget?.slotId ?? 0
            }))
        }
    }, [activeLayoutId, currentWidget, appContext.dashboard, dispatch, activeLayout])

    const selectPaneType = useCallback(() => {
        setPaneTypeMenuOpen(s => !s)
    }, [])
    const changeWidgetType = useCallback((widget: WidgetTypes) => {
        if (currentWidget?.widgetType !== widget && currentWidget?.slotId) {
            const data = {
                chartId: activeLayoutId,
                slotId: currentWidget.slotId,
                previousType: currentWidget.widgetType,
                currentType: widget
            }
            push(data, `CHANGE_WIDGET_TYPE (${widget})`, ActionType.CHANGE_WIDGET_TYPE)
            setWidgetType(widget)
            const doUpdate = (settingsOverride?: { key: string; securities: string[]; underlyingKey?: string }) => {
                if (settingsOverride) {
                    dispatch(updateWidgetSettings({
                        id: activeLayoutId,
                        slotId: currentWidget.slotId,
                        settings: settingsOverride
                    }))
                }
                dispatch(updateWidgetType({
                    id: activeLayoutId,
                    slotId: currentWidget.slotId,
                    type: widget
                }))
            }
            const needsUnderlyingKey = (currentWidget.widgetType === WidgetTypes.OPTION_CHART || currentWidget.widgetType === WidgetTypes.ATM_OPTION_CHART) &&
                (widget === WidgetTypes.CHART || widget === WidgetTypes.FUTURE_CHART)
            const needsUnderlyingKeyForOption = (currentWidget.widgetType === WidgetTypes.CHART || currentWidget.widgetType === WidgetTypes.FUTURE_CHART) &&
                (widget === WidgetTypes.OPTION_CHART || widget === WidgetTypes.ATM_OPTION_CHART)
            if (needsUnderlyingKey) {
                const underlyingKey = currentWidget.settings?.underlyingKey
                if (underlyingKey) {
                    doUpdate({ key: underlyingKey, securities: [underlyingKey] })
                } else {
                    getSecurityAsync(currentWidget.settings?.key ?? '').then((security) => {
                        const key = security?.underlyingSecurityCode
                            ? getSecurityKey(security.exchange, security.underlyingSecurityCode)
                            : currentWidget.settings?.key
                        if (key) {
                            doUpdate({ key, securities: [key] })
                        } else {
                            doUpdate()
                        }
                    })
                }
            } else if (needsUnderlyingKeyForOption) {
                const underlyingKey = currentWidget.settings?.underlyingKey ?? currentWidget.settings?.key
                if (underlyingKey) {
                    doUpdate({ key: underlyingKey, securities: [underlyingKey], underlyingKey })
                } else {
                    doUpdate()
                }
            } else {
                doUpdate()
            }
            AnalyticsManager.logEvent('Change Widget Type Clicked', {
                source: 'chart-hovered-buttons',
                widgetType: widget
            })
        }
    }, [activeLayoutId, currentWidget, dispatch])

    const handleCandleForwardBackward = useCallback((forward = true) => {
        if (timeScale) {
            const visibleRange = timeScale.getVisibleLogicalRange()
            if (visibleRange) {
                const newRange = {
                    from: forward ? visibleRange.from + 1 : visibleRange.from - 1,
                    to: forward ? visibleRange.to + 1 : visibleRange.to - 1
                }
                timeScale.setVisibleLogicalRange(newRange)
            }
            AnalyticsManager.logEvent(`Chart ${forward ? 'Forward' : 'Backward'} Clicked`, {
                source: 'chart-hovered-buttons'
            })
        }
    }, [timeScale])
    const handleZoomInOut = useCallback((zoomIn = true) => {
        if (timeScale) {
            AnalyticsManager.logEvent(`Chart Zoomed ${zoomIn ? 'In' : 'Out'} Clicked`, {
                source: 'chart-hovered-buttons'
            })
            const visibleRange = timeScale.getVisibleLogicalRange()
            const zoomWidth = 5.5
            if (visibleRange) {
                const from = zoomIn ? visibleRange.from + zoomWidth : visibleRange.from - zoomWidth
                const to = zoomIn ? visibleRange.to - zoomWidth : visibleRange.to + zoomWidth
                if (from < to) {
                    timeScale.setVisibleLogicalRange({ from, to })
                }
            }
        }
    }, [timeScale])
    const shortcuts: ShortcutType = useMemo(() => ({
        [ChartSettingsShortcutsKeyName.zoomIn]: () => {
            AnalyticsManager.logEvent('Chart Zoomed In', { source: 'keyboard' })
            handleZoomInOut()
        },
        [ChartSettingsShortcutsKeyName.zoomOut]: () => {
            AnalyticsManager.logEvent('Chart Zoomed Out', { source: 'keyboard' })
            handleZoomInOut(false)
        },
        [ChartSettingsShortcutsKeyName.moveChartLeft]: () => {
            AnalyticsManager.logEvent('Chart Moved Left', { source: 'keyboard' })
            handleCandleForwardBackward(false)
        },
        [ChartSettingsShortcutsKeyName.moveChartRight]: () => {
            AnalyticsManager.logEvent('Chart Moved Right', { source: 'keyboard' })
            handleCandleForwardBackward()
        },
        [ChartSettingsShortcutsKeyName.splitHorizontal]: (event) => {
            AnalyticsManager.logEvent('Chart Split Horizontally', { source: 'keyboard' })
            event.preventDefault()
            splitHorizontal()
        },
        [ChartSettingsShortcutsKeyName.splitVertical]: () => {
            AnalyticsManager.logEvent('Chart Split Vertically', { source: 'keyboard' })
            splitVertical()
        },
        [ChartSettingsShortcutsKeyName.deleteCurrentWidget]: () => {
            AnalyticsManager.logEvent('Chart Current Widget Deleted', { source: 'keyboard' })
            deleteCurrentWidget()
        }
    }), [handleZoomInOut, handleCandleForwardBackward, splitHorizontal, splitVertical, deleteCurrentWidget])

    useShortcuts(ShortcutsCategories.CHART_SETTINGS, shortcuts, activeWidget?.slotId === currentWidget?.slotId)

    const handleFullScreenClick = useCallback(() => {
        AnalyticsManager.logEvent('Full Screen Clicked', {
            source: 'chart-hovered-buttons',
            fullScreen: !isWidgetFullScreen
        })
        const newPaneId = activeWidget?.slotId === selectedPaneId ? null : activeWidget?.slotId ?? 0
        dispatch(setWidgetsPaneId(newPaneId))
        dispatch(toggleFullscreen())
        setOpen(false)
    }, [activeWidget, selectedPaneId, dispatch])
    const container = useSelector(selectMultiPaneFullScreen)
    const handleSplitHorizontal = useCallback(() => {
        AnalyticsManager.logEvent('Split Layout Horizontal Clicked', { source: 'chart-hovered-buttons' })
        splitHorizontal()
        setTimeout(() => {
            push(layoutRef.current, 'Split Horizontal Layout', ActionType.UPDATE_LAYOUT)
        }, 1000)
    }, [])
    const handleSplitVertical = useCallback(() => {
        AnalyticsManager.logEvent('Split Layout Vertical Clicked', { source: 'chart-hovered-buttons' })
        splitVertical()
        setTimeout(() => {
            push(layoutRef.current, 'Split Vertical Layout', ActionType.UPDATE_LAYOUT)
        }, 1000)
    }, [])

    return (
        <Box
            ref={(ele: HTMLDivElement | null) => {
                btnContainerRef.current = ele
                if (ele) ele.classList.add('float-chart-settings-button')
            }}
            sx={{
                display: 'flex',
                position: 'absolute',
                bottom: '60px',
                left: '50%',
                zIndex: 99,
                opacity: paneTypeMenuOpen ? 1 : 0,
                visibility: drawingInteractionActive ? 'hidden' : 'visible',
                ':hover': {
                    opacity: drawingInteractionActive ? 0 : '1 !important'
                },
                transform: 'translateX(-50%)',
                pointerEvents: 'none'
            }}
        >
            <div style={{ marginRight: '5px', display: 'flex' }}>
                {!activeLayout?.singleMode && (
                    <Tooltip
                        title={!isWidgetFullScreen ? 'Maximize chart' : 'Restore chart'}
                        placement="top"
                        arrow
                        open={open}
                        onOpen={() => setOpen(true)}
                        onClose={() => setOpen(false)}
                        PopperProps={{
                            container: container ? document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID) : null
                        }}

                    >
                        <span
                            onMouseEnter={() => setOpen(true)}
                            onMouseLeave={() => setOpen(false)}
                        >
                            <CustomButton variant="contained" onClick={handleFullScreenClick}>
                                {!isWidgetFullScreen ? <FullScreenIcon /> : <FullScreenReverseIcon />}
                            </CustomButton>
                        </span></Tooltip>
                )}
                {!isWidgetFullScreen && allowSplit &&  (<>
                    <NonInteractiveTooltip placement={'top'} arrow title={'Split Horizontally'}
                        PopperProps={{ container: container ? document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID) : null }}>
                        <CustomButton variant="contained" onClick={handleSplitHorizontal}><SplitHorizontalIcon
                            fontSize="small" /></CustomButton>
                    </NonInteractiveTooltip>
                    <NonInteractiveTooltip placement={'top'} arrow title={'Split Vertically'}
                        PopperProps={{ container: container ? document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID) : null }}>
                        <CustomButton variant="contained" onClick={handleSplitVertical}><SplitVerticalIcon
                            fontSize="small" /></CustomButton>
                    </NonInteractiveTooltip>
                </>)}
                {
                    !activeLayout?.singleMode &&
                    <NonInteractiveTooltip placement={'top'} arrow title={'Delete Pane'}
                        PopperProps={{ container: container ? document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID) : null }}>
                        <CustomButton variant="contained" onClick={deleteCurrentWidget}><BinIcon
                            fontSize={'small'} /></CustomButton>
                    </NonInteractiveTooltip>
                }
                <NonInteractiveTooltip placement={'top'} arrow title={'Change Widget'}
                    PopperProps={{ container: container ? document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID) : null }}>
                    <CustomButton ref={btnRef} variant="contained" onClick={selectPaneType}><Widget
                        fontSize={'small'} /></CustomButton>
                </NonInteractiveTooltip>
                <Menu
                    anchorEl={btnRef.current}
                    open={paneTypeMenuOpen}
                    anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    onClose={() => setPaneTypeMenuOpen(false)}
                >
                    {
                        Object.keys(WidgetTypesNames).map((widget, i) => {
                            if (WidgetTypesNames[widget] === '') return null;
                            return (
                                <MenuItem
                                    key={`${i}-widget-type`}
                                    disabled={(widget !== WidgetTypes.CHART && !mainsecurity?.hasFo && [ SecurityCategory_CategoryCode.STOCKS, SecurityCategory_CategoryCode.INDEX ].includes(mainsecurity?.categoryCode))
                                        || (widget !== WidgetTypes.CHART && chart?.securities?.length > 1)}
                                    selected={currentWidget?.widgetType === widget}
                                    onClick={() => changeWidgetType(widget as WidgetTypes)}>{WidgetTypesNames[widget as WidgetTypes]}</MenuItem>
                            )
                        })
                    }
                </Menu>
            </div>
            <div style={{ marginRight: '5px', display: 'flex' }}>
                <NonInteractiveTooltip placement={'top'} arrow title={'Zoom in'}
                    PopperProps={{ container: container ? document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID) : null }}>
                    <CustomButton variant="contained" onClick={() => handleZoomInOut()}><AddIcon
                        fontSize="small" /></CustomButton>
                </NonInteractiveTooltip>
                <NonInteractiveTooltip placement={'top'} arrow title={'Zoom out'}
                    PopperProps={{ container: container ? document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID) : null }}>
                    <CustomButton variant="contained" onClick={() => handleZoomInOut(false)}><SubtractIcon
                        fontSize="small" /></CustomButton>
                </NonInteractiveTooltip>
            </div>
            <div style={{ marginRight: '5px', display: 'flex' }}>
                <NonInteractiveTooltip placement={'top'} arrow title={'Scroll to the left'}
                    PopperProps={{ container: container ? document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID) : null }}>
                    <CustomButton variant="contained"
                        onClick={() => handleCandleForwardBackward(false)}><ThinArrowLeftIcon
                            fontSize="small" /></CustomButton>
                </NonInteractiveTooltip>
                <NonInteractiveTooltip placement={'top'} arrow title={'Scroll to the right'}
                    PopperProps={{ container: container ? document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID) : null }}>
                    <CustomButton variant="contained" onClick={() => handleCandleForwardBackward()}><ThinArrowRightIcon
                        fontSize="small" /></CustomButton>
                </NonInteractiveTooltip>
            </div>
            {
                showResetChartButton &&
                <NonInteractiveTooltip placement={'top'} arrow title={'Reset chart view'}
                    PopperProps={{ container: container ? document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID) : null }}>
                    <CustomButton variant="contained" onClick={resetTimeScale}><Refresh
                        fontSize="small" /></CustomButton>
                </NonInteractiveTooltip>
            }
        </Box>
    )
}
export default memo(ChartSettingsButtons)